import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { CheckLoginDataApiRoaming } from '@api-roaming/models/check-login-data-api-roaming';
import { CheckLoginEmailRequestApiRoaming } from '@api-roaming/models/check-login-email-request-api-roaming';
import { CountryCodeApiRoaming } from '@api-roaming/models/country-code-api-roaming';
import { ErrorResponse } from '@api/models';
import { LoginFormField, LoginFormService } from '@auth/components/login-form/login.form.service';
import { SysLimiters } from '@core/limiters/limiters.model';
import { AppFrontSettingsProp, ApplicationLinksModel } from '@core/models/client.settings.model';
import { SettingsAsObject } from '@core/models/settings.model';
import { RouteEntity } from '@core/router/router.entity';
import { AppSettingsService } from '@core/services/app-settings.service';
import { TranslateModule } from '@ngx-translate/core';
import {
    AccountModeSwitcherComponent,
    AccountModeType,
} from '@shared/components/account-mode-switcher/account-mode-switcher.component';
import { CountrySelectorComponent } from '@shared/components/country-selector/country-selector.component';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { IMaskDirective } from 'angular-imask';


/**
 * Login form component
 */
@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatCardModule,
        FormErrorComponent,
        TranslateModule,
        AccountModeSwitcherComponent,
        CountrySelectorComponent,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        IMaskDirective,
        MatIconModule,
        MatButtonModule,
        RouterLink,

    ],
    providers: [LoginFormService],
})
export class LoginFormComponent implements OnInit {
    /**
     * Request process indicator
     */
    @Input() public pending: boolean;
    /**
     * Server Error Object
     */
    @Input()
    public error: ErrorResponse | null = null;

    /**
     * Instance settings object from DB
     */
    @Input()
    public settings: SettingsAsObject;

    /**
     * List of country phone codes
     */
    @Input()
    public countryCodes: CountryCodeApiRoaming[] = [];

    /**
     * Default number code
     */
    @Input()
    public set defaultCode(code: CountryCodeApiRoaming) {
        if (code) {
            this.codeControl.setValue(code.code);
        }
    }

    @ViewChild('phoneInput') public phoneInput: ElementRef<HTMLInputElement>;
    /**
     * Form submission
     */
    @Output()
    public signInByEmail: EventEmitter<CheckLoginEmailRequestApiRoaming> = new EventEmitter<CheckLoginEmailRequestApiRoaming>();

    @Output()
    public signInByPhone: EventEmitter<CheckLoginDataApiRoaming> = new EventEmitter<CheckLoginDataApiRoaming>();

    @Output()
    public signInByGoogle: EventEmitter<void> = new EventEmitter<void>();

    readonly #loginFormService: LoginFormService = inject(LoginFormService);
    readonly #appSettingsService: AppSettingsService = inject(AppSettingsService);

    public readonly enableGoogle: ApplicationLinksModel = this.#appSettingsService.getPropDesign(AppFrontSettingsProp.GoogleAuth);
    public readonly passwordMinLengthLegacy: number = SysLimiters.PasswordMinLength;
    public readonly form: FormGroup = this.#loginFormService.createReactiveForm();
    public readonly loginFormField: typeof LoginFormField = LoginFormField;

    /**
     * PHONE / EMAIL mode flag
     */
    public mode: AccountModeType;
    /**
     * Flag hide/show password
     */
    public hidePassword: boolean = true;

    public readonly routeEntity: typeof RouteEntity = RouteEntity;
    public readonly accountModeType: typeof AccountModeType = AccountModeType;

    public get codeControl(): AbstractControl {
        return this.form.get(LoginFormField.Code);
    }

    public get phoneControl(): AbstractControl {
        return this.form.get(LoginFormField.Phone);
    }

    public get emailControl(): AbstractControl {
        return this.form.get(LoginFormField.Email);
    }

    public get passwordControl(): AbstractControl {
        return this.form.get(LoginFormField.Password);
    }

    public ngOnInit(): void {
        this.error = null;
        this.handlerChangeMode(AccountModeType.Phone);
    }

    public onCodeSelect(): void {
        this.phoneInput.nativeElement.focus();
    }

    public onSubmit(): void {
        if (this.form.valid) {
            const prefix: string = ('00' + this.codeControl.value).slice(-3);

            if (this.mode === AccountModeType.Phone) {
                this.signInByPhone.emit({
                    phone: this.phoneControl.value ? prefix + this.phoneControl.value : null,
                    password: this.passwordControl.value,
                });
            } else {
                this.signInByEmail.emit({
                    email: this.emailControl.value,
                    password: this.passwordControl.value,
                });
            }
        }
    }

    public handlerChangeMode(mode: AccountModeType): void {
        if (this.mode !== mode) {
            this.error = null;
            this.mode = mode;

            this.#loginFormService.setLoginMode(mode);
        }
    }
}
